import React, { useEffect, useState } from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Quote, TrendingUp } from "lucide-react";
import { api } from "@/api";

// Static fallback testimonials - shown only when no approved testimonials in DB yet
const FALLBACK_TESTIMONIALS = [
  { nom: "Lucie Marchand", role_label: "Gérante, Boulangerie Marchand", ville: "Lyon (69)", stat: "+14h", stat_label: "gagnées / mois", quote: "Avant PAG, je passais mes dimanches à faire les plannings sur Excel. Aujourd'hui, c'est fait en 15 minutes le lundi matin.", color: "#002FA7", initiales: "LM", dark_text: false },
  { nom: "Karim Bensalem", role_label: "Dirigeant, Bensalem Peinture SARL", ville: "Marseille (13)", stat: "0€", stat_label: "d'erreur de paie", quote: "Le calcul automatique heures × taux horaire a éliminé toutes les erreurs de salaire. Mes 8 salariés sont sereins.", color: "#0A0A0A", initiales: "KB", dark_text: false },
  { nom: "Sophie Renard", role_label: "Directrice, Crèche Les Lucioles", ville: "Nantes (44)", stat: "100%", stat_label: "bulletins dématérialisés", quote: "Fini les enveloppes et les oublis. Chaque salarié télécharge son bulletin depuis son espace, de n'importe où.", color: "#FFD700", initiales: "SR", dark_text: true },
  { nom: "Jean-Philippe Durand", role_label: "Restaurateur, Le Comptoir de Jean", ville: "Bordeaux (33)", stat: "+12h", stat_label: "gagnées / mois", quote: "La formation Parc Avenue m'a tout appris en 2h. Depuis, je pilote mes 15 saisonniers sans aucune galère administrative.", color: "#059669", initiales: "JD", dark_text: false },
  { nom: "Amina Diallo", role_label: "Cogérante, Diallo Services Express", ville: "Strasbourg (67)", stat: "48h", stat_label: "de relance auto", quote: "Les demandes de congé validées en un clic, les notifications instantanées. Mon équipe est alignée au quotidien.", color: "#E60000", initiales: "AD", dark_text: false },
];

export default function Testimonials() {
  const [items, setItems] = useState(FALLBACK_TESTIMONIALS);

  useEffect(() => {
    api.get("/public/testimonials")
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length > 0) {
          // Use real testimonials, padding with fallback if less than 3 to keep carousel rich
          const real = r.data.map((t) => ({
            nom: t.nom,
            prenom: t.prenom,
            role_label: t.role_label,
            ville: t.ville || "",
            stat: t.stat,
            stat_label: t.stat_label,
            quote: t.quote,
            color: t.color,
            initiales: t.initiales,
            dark_text: t.dark_text,
          }));
          setItems(real.length >= 3 ? real : [...real, ...FALLBACK_TESTIMONIALS].slice(0, 5));
        }
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  return (
    <section className="border-b border-[#DEE2E6] bg-[#F8F9FA]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#002FA7] font-semibold mb-3">// Témoignages</div>
            <h2 className="font-display text-3xl lg:text-5xl font-black tracking-tight leading-[1]">
              Des chefs d'entreprise<br />
              <span className="text-[#002FA7]">conquis.</span>
            </h2>
          </div>
          <div className="text-sm text-[#495057] max-w-sm">
            Plus de 500 dirigeants utilisent PAG chaque jour pour piloter leur entreprise. Voici ce qu'ils en disent.
          </div>
        </div>

        <Carousel
          opts={{ align: "start", loop: true }}
          className="w-full"
          data-testid="testimonials-carousel"
        >
          <CarouselContent className="-ml-4">
            {items.map((t, i) => (
              <CarouselItem key={i} className="pl-4 md:basis-1/2 lg:basis-1/3">
                <TestimonialCard t={t} testid={`testimonial-${i}`} />
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex items-center gap-3 mt-8">
            <CarouselPrevious className="static translate-y-0 h-11 w-11 rounded-none border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white" data-testid="testimonials-prev" />
            <CarouselNext className="static translate-y-0 h-11 w-11 rounded-none border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white" data-testid="testimonials-next" />
            <div className="text-xs uppercase tracking-[0.2em] text-[#868E96] font-semibold ml-4">
              Glissez ou utilisez les flèches
            </div>
          </div>
        </Carousel>
      </div>
    </section>
  );
}

function TestimonialCard({ t, testid }) {
  const fullName = t.prenom ? `${t.prenom} ${t.nom}` : t.nom;
  return (
    <div className="h-full bg-white border border-[#DEE2E6] p-6 flex flex-col gap-5 elev-hover" data-testid={testid}>
      {/* Stat badge */}
      {t.stat && (
        <div
          className="flex items-center justify-between p-4 -mx-6 -mt-6 border-b border-[#DEE2E6]"
          style={{ backgroundColor: t.color, color: t.dark_text ? "#0A0A0A" : "white" }}
        >
          <div className="flex items-baseline gap-2">
            <TrendingUp className="h-5 w-5" />
            <div className="font-display text-3xl font-black tracking-tighter">{t.stat}</div>
          </div>
          {t.stat_label && (
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold opacity-80 text-right leading-tight">
              {t.stat_label}
            </div>
          )}
        </div>
      )}

      <Quote className="h-6 w-6 text-[#002FA7] flex-shrink-0" />
      <p className="text-sm lg:text-base text-[#0A0A0A] leading-relaxed font-medium flex-1">
        « {t.quote} »
      </p>

      <div className="flex items-center gap-3 pt-4 border-t border-[#DEE2E6]">
        <div
          className="h-10 w-10 flex items-center justify-center font-display font-black text-sm"
          style={{ backgroundColor: t.color, color: t.dark_text ? "#0A0A0A" : "white" }}
        >
          {t.initiales}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm tracking-tight truncate">{fullName}</div>
          <div className="text-xs text-[#495057] truncate">{t.role_label}</div>
          {t.ville && <div className="text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-semibold">{t.ville}</div>}
        </div>
      </div>
    </div>
  );
}
